import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown, FiCheck } from 'react-icons/fi';
import './style.css';

const DROPDOWN_MAX_HEIGHT = 260;

function Select({
  id,
  label,
  options = [],
  className = '',
  searchable = false,
  useCustomDropdown = false,
  multiple = false,
  icon: Icon,
  value,
  onChange,
  placeholder,
  error,
  ...rest
}) {
  const normalizedOptions = useMemo(
    () => (Array.isArray(options) ? options : []),
    [options]
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: null, bottom: null, left: 0, width: 0, openUpward: false });
  
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const isClosingByBack = useRef(false);

  useEffect(() => {
    if (isOpen) {
      window.history.pushState({ selectOpen: id || 'select' }, '');
      
      const handlePopState = () => {
        isClosingByBack.current = true;
        setIsOpen(false);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        const currentState = window.history.state;
        if (!isClosingByBack.current && currentState && currentState.selectOpen === (id || 'select')) {
          window.history.back();
        }
        isClosingByBack.current = false;
      };
    }
  }, [isOpen, id]);

  useEffect(() => {
    if (!searchable && !useCustomDropdown) {
      setSearchTerm('');
      setIsOpen(false);
    }
  }, [searchable, useCustomDropdown]);

  useEffect(() => {
    if (isOpen && wrapperRef.current) {
      const updatePosition = () => {
        const rect = wrapperRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const openUpward = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;

        if (openUpward) {
          setCoords({
            bottom: window.innerHeight - rect.top + 4,
            top: null,
            left: rect.left,
            width: rect.width,
            openUpward: true,
          });
        } else {
          setCoords({
            top: rect.bottom + 4,
            bottom: null,
            left: rect.left,
            width: rect.width,
            openUpward: false,
          });
        }
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if ((!searchable && !useCustomDropdown) || !isOpen) {
      return;
    }

    function handleClickOutside(event) {
      if (
        wrapperRef.current && 
        !wrapperRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        document.body.setAttribute('data-dropdown-closed', 'true');
        setTimeout(() => {
          document.body.removeAttribute('data-dropdown-closed');
        }, 200);
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchable, isOpen]);

  const selectedOption = useMemo(() => {
    if (multiple) {
      if (!Array.isArray(value)) return [];
      const stringValues = value.map(v => String(v));
      return normalizedOptions.filter(option => {
        const optVal = option.value ?? option;
        return stringValues.includes(String(optVal));
      });
    }
    return (
      normalizedOptions.find(option => {
        const optVal = option.value ?? option;
        if (value === null || value === undefined || value === '') {
             return optVal === value || optVal === '' || optVal === null;
        }
        return String(optVal) === String(value);
      }) || null
    );
  }, [normalizedOptions, value, multiple]);

  const selectedOptionLabel = useMemo(() => {
    if (multiple) {
      if (!selectedOption || selectedOption.length === 0) return '';
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
          {selectedOption.map((opt) => (
            <span key={opt.value ?? opt} style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontSize: '0.85em' }}>
              {opt.label ?? opt}
            </span>
          ))}
        </div>
      );
    }
    return selectedOption ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {selectedOption.icon && <selectedOption.icon size={16} />}
        {selectedOption.color && (
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: selectedOption.color, flexShrink: 0 }} />
        )}
        {selectedOption.label ?? selectedOption}
      </div>
    ) : '';
  }, [selectedOption, multiple]);

  const filteredOptions = searchable
    ? normalizedOptions.filter(option => {
        const labelText = (option.label ?? option).toString().toLowerCase();
        const valueText = (option.value ?? option).toString().toLowerCase();
        const term = searchTerm.toLowerCase();
        return labelText.includes(term) || valueText.includes(term);
      })
    : normalizedOptions;

  function handleSelectChange(event) {
    if (onChange) {
      onChange(event);
    }
  }

  function handleOptionSelect(option) {
    const optionValue = option.value ?? option;

    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      let newValues;
      if (currentValues.includes(optionValue)) {
        newValues = currentValues.filter(v => v !== optionValue);
      } else {
        newValues = [...currentValues, optionValue];
      }

      if (onChange) {
        onChange({
          target: {
            id,
            value: newValues,
          },
        });
      }
    } else {
      if (onChange) {
        onChange({
          target: {
            id,
            value: optionValue,
          },
        });
      }
      setSearchTerm('');
      setIsOpen(false);
    }
  }

  const dropdownStyle = {
    position: 'fixed',
    left: coords.left,
    width: coords.width,
    zIndex: 99999,
    ...(coords.openUpward
      ? { bottom: coords.bottom }
      : { top: coords.top }),
  };

  if (!searchable && !useCustomDropdown) {
    return (
      <div className={`field ${className}`}>
        {label && (
          <label className="field-label" htmlFor={id}>
            {label}
          </label>
        )}

        <div className="input-wrapper">
          {Icon && (
            <div className="input-icon">
              <Icon size={18} />
            </div>
          )}
          <select
            id={id}
            className={`field-select ${Icon ? 'has-icon' : ''} ${error ? 'has-error' : ''}`}
            value={value}
            onChange={handleSelectChange}
            {...rest}
          >
            {normalizedOptions.map(option => (
              <option
                key={option.value ?? option}
                value={option.value ?? option}
              >
                {option.label ?? option}
              </option>
            ))}
          </select>
          <div className="select-chevron">
            <FiChevronDown size={16} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`field ${className}`}>
      {label && (
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
      )}

      <div ref={wrapperRef} className="input-wrapper searchable-select">
        {Icon && (
          <div className="input-icon">
            <Icon size={18} />
          </div>
        )}
        <div
          id={id}
          className={`field-select ${Icon ? 'has-icon' : ''} ${error ? 'has-error' : ''}`}
          style={{ cursor: rest.disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => !rest.disabled && setIsOpen(prev => !prev)}
        >
          {selectedOptionLabel || <span style={{ color: '#9ca3af' }}>{placeholder}</span>}
        </div>
        <div
          className={`select-chevron ${isOpen ? 'open' : ''}`}
          onClick={() => !rest.disabled && setIsOpen(prev => !prev)}
        >
          <FiChevronDown size={16} />
        </div>

        {isOpen && createPortal(
          <div 
            ref={dropdownRef}
            className={`select-dropdown ${coords.openUpward ? 'open-upward' : ''}`}
            style={dropdownStyle}
          >
            {searchable && (
              <input
                className="select-search-input"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                autoFocus
              />
            )}
            <div className="select-options">
              {filteredOptions.map(option => {
                const optionValue = option.value ?? option;
                const optionLabel = option.label ?? option;
                let selected;
                
                if (multiple) {
                  selected = Array.isArray(value) && value.includes(optionValue);
                } else {
                  selected = String(optionValue) === String(value);
                }

                return (
                  <div
                    key={optionValue}
                    className={`select-option ${selected ? 'option-selected' : ''}`}
                    onMouseDown={event => {
                      event.preventDefault();
                      handleOptionSelect(option);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    {multiple && (
                      <div style={{ 
                        width: 16, 
                        height: 16, 
                        border: '1px solid #d1d5db', 
                        borderRadius: 3, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        background: selected ? 'var(--primary-color)' : 'white',
                        borderColor: selected ? 'var(--primary-color)' : '#d1d5db'
                      }}>
                        {selected && <FiCheck size={12} color="white" />}
                      </div>
                    )}
                    {option.icon && <option.icon size={16} />}
                    {option.color && (
                      <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: option.color, flexShrink: 0 }} />
                    )}
                    {optionLabel}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}

export default Select;